/**
 * 文件定位：
 * 1. 当前文件位于 `cad-core/manager/pick/utils` 工具目录；
 * 2. 负责为屏幕空间拾取提供与渲染后端无关的运行时结构守卫；
 * 3. 该文件的目标是消除 `three` 与 `three/webgpu` 混用时的构造器耦合。
 */

/**
 * 可被屏幕空间线拾取逻辑消费的位置属性协议。
 */
export interface PositionAttributeLike {
    /** 顶点数量。 */
    count: number;
    /**
     * 读取指定索引顶点的 X 坐标。
     * @param index 顶点索引。
     * @returns X 坐标。
     */
    getX(index: number): number;
    /**
     * 读取指定索引顶点的 Y 坐标。
     * @param index 顶点索引。
     * @returns Y 坐标。
     */
    getY(index: number): number;
    /**
     * 读取指定索引顶点的 Z 坐标。
     * @param index 顶点索引。
     * @returns Z 坐标。
     */
    getZ(index: number): number;
}

/**
 * 判断目标值是否满足位置属性协议。
 *
 * 设计说明：
 * 1. 这里只验证拾取所必需的最小结构，而不是依赖具体渲染后端的类构造器；
 * 2. 这样可以兼容 `three` 与 `three/webgpu` 创建出的 `BufferAttribute` 实例；
 * 3. 若结构不满足要求，调用方应直接跳过该候选，避免在热路径中抛错。
 *
 * @param value 待判定值。
 * @returns 是否满足位置属性协议。
 */
export function isPositionAttributeLike(value: unknown): value is PositionAttributeLike {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const count = Reflect.get(value, 'count');
    const getX = Reflect.get(value, 'getX');
    const getY = Reflect.get(value, 'getY');
    const getZ = Reflect.get(value, 'getZ');

    return Number.isInteger(count)
        && Number(count) >= 0
        && typeof getX === 'function'
        && typeof getY === 'function'
        && typeof getZ === 'function';
}

/**
 * 从任意材质对象中解析有限的 `linewidth`。
 *
 * 设计说明：
 * 1. 屏幕空间线拾取只依赖 `linewidth` 数值，不应关心材质来自哪条构造链；
 * 2. 通过结构读取而非 `instanceof`，可以避免跨包构造器导致的误判；
 * 3. 若材质未提供有效宽度，调用方应回退到默认拾取宽度。
 *
 * @param material 材质对象。
 * @returns 有效线宽；不存在或非法时返回 `null`。
 */
export function resolveFiniteLinewidth(material: unknown): number | null {
    if (typeof material !== 'object' || material === null) {
        return null;
    }

    const linewidth = Reflect.get(material, 'linewidth');
    if (typeof linewidth !== 'number' || !Number.isFinite(linewidth)) {
        return null;
    }

    return linewidth;
}

/**
 * 从任意对象中解析有限的 `scale.x`。
 *
 * 设计说明：
 * 1. 点拾取只关心渲染后的显示缩放，不应依赖材质具体类型；
 * 2. 当前仓库中的点对象同时可能来自 `three` 与 `three/webgpu`；
 * 3. 通过结构守卫可以让拾取逻辑仅依赖显示结果本身。
 *
 * @param value 待读取对象。
 * @returns 有效的 `scale.x`；无法解析时返回 `null`。
 */
export function resolveFiniteScaleX(value: unknown): number | null {
    if (typeof value !== 'object' || value === null) {
        return null;
    }

    const scale = Reflect.get(value, 'scale');
    if (typeof scale !== 'object' || scale === null) {
        return null;
    }

    const scaleX = Reflect.get(scale, 'x');
    if (typeof scaleX !== 'number' || !Number.isFinite(scaleX)) {
        return null;
    }

    return scaleX;
}
